/**
 * 种子化伪随机源（确定性仿真基石）。
 *
 * 设计要点（设计文档 D6 / 风险表第 4 项）：
 * - mulberry32 算法，约 50 行，自实现避免外部依赖；
 * - 命名随机流：同一 master 种子 + 流名 => 同一序列，流与流之间互相独立，
 *   新增一类随机消费不扰动其他序列（AC-F006-02 语义）；
 * - 全程禁用 Math.random / Date.now，保证可复现。
 */

function hashString(str: string): number {
  // FNV-1a 32-bit
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** 将两个 uint32 混合为一个 uint32（avalanche）。 */
function mix32(a: number, b: number): number {
  let x = (a ^ b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  return (x ^ (x >>> 16)) >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return (t ^ (t >>> 14)) >>> 0;
  };
}

/** 单条随机流。 */
export class RngStream {
  private readonly next32: () => number;

  constructor(seed: number) {
    this.next32 = mulberry32(seed >>> 0);
  }

  /** 返回 uint32 ∈ [0, 2^32)。 */
  nextUInt32(): number {
    return this.next32();
  }

  /** 返回 float ∈ [0, 1)。 */
  next(): number {
    return this.next32() / 4294967296;
  }

  /** 返回 int ∈ [0, maxExclusive)。 */
  nextInt(maxExclusive: number): number {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
      throw new Error(`nextInt: maxExclusive must be a positive integer, got ${maxExclusive}`);
    }
    return Math.floor(this.next() * maxExclusive);
  }

  /** 从非空数组中随机取一个元素。 */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('pick: items must be non-empty');
    return items[this.nextInt(items.length)]!;
  }

  /**
   * 指数分布变体（客流到达间隔）。
   * @param ratePerSec 到达率（次/秒）
   * @returns 到达间隔（秒）；rate<=0 时返回 Infinity（无到达）
   */
  exponential(ratePerSec: number): number {
    if (ratePerSec <= 0) return Infinity;
    // 设计文档 3.3：interval = -ln(1 − rng.next()) / λ
    return -Math.log(1 - this.next()) / ratePerSec;
  }
}

/** 命名随机流族。 */
export class SeededRng {
  readonly masterSeed: number;

  constructor(masterSeed: number) {
    this.masterSeed = masterSeed >>> 0;
  }

  /** 派生一条命名流；同 masterSeed + 同 name => 同序列。 */
  stream(name: string): RngStream {
    return new RngStream(mix32(this.masterSeed, hashString(name)));
  }
}
